<template>
  <li
    v-show="shouldShowItem"
    class="not-last:mb-2"
    role="none"
  >
    <!-- Item with children (submenu) -->
    <template v-if="item.children && item.children.length > 0">
      <button
        :class="[
          defaultButtonClasses,
          {
            'justify-center': !sidebarIsExpanded,
            '!bg-red-1 !text-white': sidebarStore.routeIsActive(item.name),
          },
        ]"
        class="flex items-center cursor-pointer w-full"
        :aria-expanded="item.meta.is_expanded"
        :aria-controls="`submenu-${getItemKey(item)}`"
        :aria-label="`${item.meta.title} submenu`"
        role="menuitem"
        @click="handleSubmenuClick"
        @keydown.enter="handleSubmenuClick"
      >
        <span
          v-if="item.meta.permissions_needed && level === 0"
          :class="[
            'rounded-full p-1',
            sidebarStore.routeIsActive(item.name)
              ? 'bg-red-5 text-white'
              : 'bg-grey-9 dark:bg-dark-surface-2 text-grey-11 dark:text-dark-ink-muted',
          ]"
        >
          <mdicon aria-label="Secured Route" size="18" name="lock-outline" />
        </span>
        <mdicon v-else :aria-label="item.meta.title" :name="item.meta?.icon" />

        <span v-if="sidebarIsExpanded" class="text-left ml-4 grow">
          {{ item.meta.title }}
        </span>
        
        <mdicon
          v-if="sidebarIsExpanded"
          name="chevron-right"
          size="18"
          aria-hidden="true"
          class="ml-2"
        />
      </button>
    </template>

    <!-- Leaf item (no children) -->
    <router-link
      v-else
      :to="{ name: item.name }"
      :aria-label="item.meta.title"
      class="flex items-center"
      :class="[
        {
          'justify-center': !sidebarIsExpanded,
          '!bg-red-1 !text-white': sidebarStore.routeIsActive(item.name),
        },
        defaultButtonClasses,
      ]"
      role="menuitem"
      tabindex="0"
      @click="handleMobileNavigation"
      @keydown.enter="handleMobileNavigation"
    >
      <span
        v-if="item.meta.permissions_needed && level === 0"
        :class="[
          'rounded-full p-1',
          sidebarStore.routeIsActive(item.name)
            ? 'bg-red-5 text-white'
            : 'bg-grey-9 dark:bg-dark-surface-2 text-grey-11 dark:text-dark-ink-muted',
        ]"
      >
        <mdicon aria-label="Secured Route" size="18" name="lock-outline" />
      </span>
      <mdicon v-else :aria-label="item.meta.title" :name="item.meta?.icon" />

      <span v-if="sidebarIsExpanded" class="ml-4 grow">
        {{ item.meta.title }}
      </span>
    </router-link>
  </li>
</template>

<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useSidebarNavigation } from '../../../composables/useSidebarNavigation.js'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  level: {
    type: Number,
    default: 0,
  },
})

const {
  sidebarStore,
  sidebarIsExpanded,
  getItemKey,
  handleMobileNavigation,
  handleSubmenuExpansion,
  defaultButtonClasses,
} = useSidebarNavigation()

const { hasExpandedSubmenu } = storeToRefs(sidebarStore)

/**
 * Determine if this item should be visible
 * At level 0, hide if any submenu is expanded and this item is not expanded
 * At deeper levels, always show
 */
const shouldShowItem = computed(() => {
  if (props.level === 0) {
    return !hasExpandedSubmenu.value || props.item.meta.is_expanded
  }
  return true
})

/**
 * Handle submenu click - expand the submenu
 */
const handleSubmenuClick = () => {
  handleSubmenuExpansion(props.item)
}
</script>
